/**
 * The Tixologi route that renders every ticket of a transaction as ONE PDF: letter size, or
 * the thermal box-office template with `boxOffice`. A path only; the caller adds its base URL.
 */
export const transactionPdfPath = (
  transactionId: string | number,
  { boxOffice = false }: { boxOffice?: boolean } = {}
): string =>
  `/download/pdf_tickets/${transactionId}${boxOffice ? "?boxOffice=true" : ""}`;

/**
 * The Tixologi route that renders one ticket as a thermal PDF (there is no letter-size
 * per-ticket template). A path only; the caller adds its base URL.
 */
export const ticketPdfPath = (ticketId: string | number): string =>
  `/download/pdf_ticket/${ticketId}`;

/**
 * Download tickets as PDF
 */
export async function downloadTicketsPDF(params: {
  transactionCode: string;
  token: string;
}): Promise<Blob> {
  const { transactionCode, token } = params;

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}${transactionPdfPath(transactionCode)}`,
      {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error downloading PDF tickets: ${response.status} - ${errorText}`
      );
    }

    return response.blob();
  } catch (error) {
    console.error("Error downloading PDF tickets:", error);
    throw error;
  }
}

/**
 * Download tickets as Apple Wallet passes
 */
export async function downloadAppleWallet(params: {
  transactionCode: string;
  ticketCount: number;
  token: string;
}): Promise<Blob> {
  const { transactionCode, ticketCount, token } = params;

  try {
    const acceptHeader =
      ticketCount > 1
        ? "application/vnd.apple.pkpasses"
        : "application/vnd.apple.pkpass";

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/download/apple_wallet/${transactionCode}`,
      {
        headers: {
          Accept: acceptHeader,
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error downloading Apple Wallet passes: ${response.status} - ${errorText}`
      );
    }

    return response.blob();
  } catch (error) {
    console.error("Error downloading Apple Wallet passes:", error);
    throw error;
  }
}

/**
 * Utility function to trigger file download in the browser
 */
export function triggerFileDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Utility function to trigger Apple Wallet pass download
 * Uses download link approach to avoid page navigation and memory leaks
 */
export function openAppleWallet(blob: Blob, contentType: string): void {
  const url = window.URL.createObjectURL(
    new Blob([blob], { type: contentType })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `tickets.${contentType.includes("pkpasses") ? "pkpasses" : "pkpass"}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
